import { ref, Ref } from "vue";

export const useDrag = (
  options: {
    isDraggingMode: Ref<boolean>;
    scrollContainer: any;
  },
  callback: (originIndex: number, targetIndex: number) => void
) => {
  const { isDraggingMode, scrollContainer } = options;
  const dragIndex = ref(-1);
  const dragTargetIndex = ref(-1);

  const touchStartY = ref(0);
  const touchEl = ref<HTMLElement | null>(null);
  const touchContainerSize = ref(0);
  let isDragging = false;

  // 添加长按定时器相关变量
  let longPressTimer: number | null = null;
  const LONG_PRESS_DURATION = 200; // 长按触发时间，单位毫秒

  // 添加自动滚动相关变量
  let autoScrollInterval: number | null = null;
  const SCROLL_SPEED = 5; // 滚动速度（像素/帧）
  const SCROLL_THRESHOLD = 100; // 触发自动滚动的边界阈值（像素）

  const startAutoScroll = (direction: "up" | "down") => {
    if (autoScrollInterval) return;

    autoScrollInterval = window.setInterval(() => {
      const scrollAmount = direction === "up" ? -SCROLL_SPEED : SCROLL_SPEED;
      let container = scrollContainer;
      if (container.value) {
        container = container.value;
      }
      if (!container?.scrollBy) container = container.$el;
      container!.scrollBy(0, scrollAmount);
    }, 16); // 约60fps
  };

  const stopAutoScroll = () => {
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
      autoScrollInterval = null;
    }
  };

  const dragStart = (e: DragEvent, index: number) => {
    if (!isDraggingMode.value) return;
    e.preventDefault();
    isDragging = true;
    dragIndex.value = index;
    dragTargetIndex.value = index;
  };

  const dragEnter = (e: DragEvent, index: number) => {
    if (!isDraggingMode.value) return;
    e.preventDefault();
    if (!isDragging) return;
    dragTargetIndex.value = index;
  };

  const dragOver = (e: DragEvent) => {
    if (!isDraggingMode.value) return;
    e.preventDefault();
    if (!isDragging) return;
  };

  const dragEnd = () => {
    if (!isDraggingMode.value) return;
    if (!isDragging) return;
    isDragging = false;
    callback(dragIndex.value, dragTargetIndex.value);
    dragIndex.value = -1;
    dragTargetIndex.value = -1;
  };

  const touchStart = (e: TouchEvent, index: number, length: number) => {
    if (!isDraggingMode.value) return;

    // 设置长按定时器
    longPressTimer = window.setTimeout(() => {
      e.preventDefault();
      isDragging = true;
      const droppableElement = getDroppableElement(e);
      if (droppableElement) {
        touchEl.value = e.target as HTMLElement;
        dragIndex.value = index;
        dragTargetIndex.value = index;
        touchStartY.value = e.touches[0].clientY;
        touchContainerSize.value = length;
      }
    }, LONG_PRESS_DURATION);
  };

  const touchMove = (e: TouchEvent) => {
    if (!isDraggingMode.value) return;

    if (!isDragging) {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      return;
    }

    e.preventDefault();
    if (!touchEl.value) return;

    // 获取触摸点相对于视窗的位置
    const touch = e.touches[0];
    const touchY = touch.clientY;
    const viewportHeight = window.innerHeight;

    // 检查是否需要自动滚动
    if (touchY < SCROLL_THRESHOLD) {
      startAutoScroll("up");
    } else if (touchY > viewportHeight - SCROLL_THRESHOLD) {
      startAutoScroll("down");
    } else {
      stopAutoScroll();
    }

    const droppableElement = getDroppableElement(e);
    if (droppableElement) {
      const newIndex = parseInt(
        droppableElement.getAttribute("data-index") || "-1"
      );
      if (newIndex >= 0 && newIndex < touchContainerSize.value) {
        dragTargetIndex.value = newIndex;
      }
    }
  };

  const getDroppableElement = (e: TouchEvent) => {
    const touch = e.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    return elements.find(
      (el) =>
        el.hasAttribute("draggable") && el.getAttribute("draggable") === "true"
    );
  };

  const touchEnd = () => {
    if (!isDraggingMode.value) return;

    // 清除长按定时器
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }

    // 停止自动滚动
    stopAutoScroll();

    if (!touchEl.value) return;
    if (!isDragging) return;
    isDragging = false;
    callback(dragIndex.value, dragTargetIndex.value);
    touchEl.value = null;
    dragIndex.value = -1;
    dragTargetIndex.value = -1;
  };

  return {
    dragIndex,
    dragTargetIndex,
    dragStart,
    dragEnd,
    dragEnter,
    dragOver,
    touchStart,
    touchMove,
    touchEnd,
  };
};
